<script lang="ts">
  import type { VideoData, User } from '../lib/types'
  import { hasPermission } from '../lib/types'

  interface Props {
    videoTitle: string
    videoData: VideoData | null
    currentUser: User | null
    isAnalyzing: boolean
    analysisProgress: number
    analysisStage: string | null
    analysisError: string | null
    isTranscribing: boolean
    transcribeProgress: number
    transcribeError: string | null
    transcribeElapsedSeconds: number
    transcribeEstimateSeconds: number
    onOpenVideoInfo: () => void
    onAnalyze: () => void
    onCancelAnalysis: () => void
    onDismissAnalysisError: () => void
    onTranscribe: () => void
    onDismissTranscribeError: () => void
    onToggleAutoAnalyze: (enabled: boolean) => void
    autoAnalyzeError: string | null
    onLogout: () => void
    onShowLogin: () => void
  }

  let {
    videoTitle,
    videoData,
    currentUser,
    isAnalyzing,
    analysisProgress,
    analysisStage,
    analysisError,
    isTranscribing,
    transcribeProgress,
    transcribeError,
    transcribeElapsedSeconds,
    transcribeEstimateSeconds,
    onOpenVideoInfo,
    onAnalyze,
    onCancelAnalysis,
    onDismissAnalysisError,
    onTranscribe,
    onDismissTranscribeError,
    onToggleAutoAnalyze,
    autoAnalyzeError,
    onLogout,
    onShowLogin,
  }: Props = $props()

  let isQueued = $derived(isAnalyzing && analysisProgress === 0 && !analysisStage)

  function handleCancel() {
    if (confirm('Cancel the analysis in progress?')) {
      onCancelAnalysis()
    }
  }

  function formatEstimatedTime(seconds: number | undefined): string {
    if (seconds === undefined || seconds <= 0) return ''
    if (seconds < 60) return `~${seconds}s`
    const minutes = Math.round(seconds / 60)
    if (minutes < 60) return `~${minutes}min`
    const hours = Math.floor(minutes / 60)
    const restMinutes = minutes % 60
    return restMinutes === 0 ? `~${hours}h` : `~${hours}h${restMinutes}min`
  }

  function formatClock(seconds: number): string {
    const safe = Math.max(0, Math.floor(seconds))
    const mins = Math.floor(safe / 60)
    const secs = safe % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  let projectedTotalSeconds = $derived.by(() => {
    if (transcribeProgress > 0) {
      const fromProgress = Math.round((transcribeElapsedSeconds / transcribeProgress) * 100)
      return Math.max(fromProgress, transcribeEstimateSeconds, transcribeElapsedSeconds)
    }
    return Math.max(transcribeEstimateSeconds, transcribeElapsedSeconds)
  })
</script>

<header class="flex justify-between items-center px-4 h-12 bg-surface-1 border-b border-border-default shrink-0">
  <div class="flex items-center gap-3 min-w-0 flex-1">
    <h1
      class="m-0 text-base font-medium text-text-primary whitespace-nowrap overflow-hidden text-ellipsis cursor-pointer hover:text-accent-primary"
      onclick={onOpenVideoInfo}
    >
      {videoTitle || 'Outer Screen'}
    </h1>
  </div>

  <div class="flex items-center gap-2 shrink-0">
    {#if videoData?.transcript && hasPermission(currentUser, 'can_analyze_videos')}
      {#if isAnalyzing}
        <span
          class="flex items-center gap-2 h-7 px-3 text-sm rounded whitespace-nowrap"
          class:bg-accent-primary={!isQueued}
          class:bg-yellow-500={isQueued}
          class:bg-opacity-15={true}
          title={analysisStage || (isQueued ? 'Queued on Tank...' : 'Processing...')}
        >
          {#if isQueued}
            <span aria-hidden="true">⏳</span>
            <span class="text-yellow-300">Queued</span>
          {:else}
            <span aria-hidden="true">⏱</span>
            <span class="inline-block w-[60px] h-1.5 bg-blue-200/20 rounded overflow-hidden">
              <span class="block h-full bg-blue-300 rounded transition-[width]" style:width="{analysisProgress}%"></span>
            </span>
            <span class="text-blue-300 min-w-[32px] text-right">{analysisProgress}%</span>
          {/if}
          <button class="ml-1 text-blue-300/60 hover:text-red-400" onclick={handleCancel} title="Cancel analysis">✕</button>
        </span>
      {:else if analysisError}
        <span class="flex items-center gap-1.5 h-7 px-3 text-sm bg-red-500/15 border border-red-500/40 rounded text-red-300 whitespace-nowrap" title={analysisError}>
          <span aria-hidden="true">!</span>
          <span>Failed</span>
          <button class="ml-1 text-red-300/70 hover:text-red-200" aria-label="Dismiss error" onclick={onDismissAnalysisError}>✕</button>
        </span>
      {:else if !videoData.analyzed}
        <button
          class="h-7 px-3 text-sm bg-surface-3 border border-border-default rounded text-text-secondary cursor-pointer whitespace-nowrap hover:enabled:bg-surface-4 hover:enabled:border-border-strong hover:enabled:text-text-primary disabled:opacity-50 disabled:cursor-not-allowed"
          onclick={onAnalyze}
        >
          Analyze<span class="ml-1.5 text-xs text-text-muted">{formatEstimatedTime(videoData.estimated_time)}</span>
        </button>
      {/if}
    {:else if videoData && !videoData.transcript && hasPermission(currentUser, 'can_analyze_videos')}
      {#if transcribeError}
        <span class="flex items-center gap-1.5 h-7 px-3 text-sm bg-red-500/15 border border-red-500/40 rounded text-red-300 whitespace-nowrap" title={transcribeError}>
          <span aria-hidden="true">!</span>
          <span>Transcribe failed</span>
          <button class="ml-1 text-red-300/70 hover:text-red-200" aria-label="Dismiss error" onclick={onDismissTranscribeError}>✕</button>
        </span>
      {:else if isTranscribing}
        <span class="flex items-center gap-2 h-7 px-3 text-sm bg-accent-primary/15 border border-accent-primary/40 rounded text-text-secondary whitespace-nowrap" title="Transcribing on the local machine">
          <span aria-hidden="true">{transcribeProgress > 0 ? '⏱' : '⏳'}</span>
          <span>
            Transcribing {formatClock(transcribeElapsedSeconds)}
            {#if projectedTotalSeconds > 0} / ~{formatClock(projectedTotalSeconds)}{/if}
            {#if transcribeProgress > 0} ({Math.round(transcribeProgress)}%){/if}
          </span>
        </span>
      {:else}
        <button
          class="h-7 px-3 text-sm bg-surface-3 border border-border-default rounded text-text-secondary cursor-pointer whitespace-nowrap hover:enabled:bg-surface-4 hover:enabled:border-border-strong hover:enabled:text-text-primary disabled:opacity-50 disabled:cursor-not-allowed"
          onclick={onTranscribe}
          disabled={isTranscribing}
          title="Transcribe this video on the local machine"
        >
          Transcribe<span class="ml-1.5 text-xs text-text-muted">{formatEstimatedTime(transcribeEstimateSeconds)}</span>
        </button>
      {/if}
    {/if}
    {#if videoData && (hasPermission(currentUser, 'can_analyze_videos') || hasPermission(currentUser, 'can_manage_videos'))}
      {@const autoEnabled = videoData.auto_analyze?.enabled ?? false}
      {@const channelKnown = videoData.auto_analyze?.channel_known ?? false}
      {@const channelName = videoData.auto_analyze?.channel_name}
      {@const canEnable = hasPermission(currentUser, 'can_analyze_videos')}
      {@const canDisable = hasPermission(currentUser, 'can_manage_videos')}
      {@const canToggle = autoEnabled ? canDisable : canEnable}
      <button
        class="h-7 pl-1.5 pr-3 text-sm rounded whitespace-nowrap flex items-center gap-2 border bg-surface-3 border-border-default text-text-secondary"
        class:cursor-pointer={canToggle}
        class:cursor-not-allowed={!canToggle}
        class:opacity-60={!canToggle}
        disabled={!canToggle}
        onclick={() => canToggle && onToggleAutoAnalyze(!autoEnabled)}
        aria-pressed={autoEnabled}
        title={!channelKnown
          ? (canEnable ? "This channel isn't identified yet. Click to look it up and auto-analyze its new videos." : 'Channel not identified. You need analyze rights to enable.')
          : autoEnabled
            ? (canDisable ? 'Auto-analyzing new videos from this channel. Click to turn off.' : 'Auto-analyzing new videos from this channel. Only managers can turn it off.')
            : (canEnable ? 'Off. Turn on to auto-analyze new videos from this channel.' : 'Off. You need analyze rights to turn it on.')}
      >
        <span
          class="relative inline-block w-8 h-4 rounded-full transition-colors duration-150"
          class:bg-green-500={autoEnabled}
          class:bg-surface-4={!autoEnabled && channelKnown}
          class:bg-transparent={!channelKnown}
          class:border={!channelKnown}
          class:border-dashed={!channelKnown}
          class:border-amber-400={!channelKnown}
        >
          {#if !channelKnown}
            <span class="absolute inset-0 flex items-center justify-center text-[9px] leading-none font-bold text-amber-400">?</span>
          {:else}
            <span
              class="absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white transition-transform duration-150"
              class:translate-x-4={autoEnabled}
            ></span>
          {/if}
        </span>
        {#if !channelKnown}
          <span class="text-amber-300">Identify channel to auto-analyze</span>
        {:else if channelName}
          Auto-analyze: {channelName}
        {:else}
          Auto-analyze channel
        {/if}
      </button>
      {#if autoAnalyzeError}
        <span class="text-sm text-red-300 whitespace-nowrap" title={autoAnalyzeError}>{autoAnalyzeError}</span>
      {/if}
    {/if}

    {#if hasPermission(currentUser, 'can_manage_videos')}
      <a
        href="/admin"
        class="h-7 px-3 text-sm bg-surface-3 border border-border-default rounded text-text-secondary no-underline flex items-center cursor-pointer whitespace-nowrap hover:bg-surface-4 hover:border-border-strong hover:text-text-primary"
      >
        Videos
      </a>
      <a
        href="/channels"
        class="h-7 px-3 text-sm bg-surface-3 border border-border-default rounded text-text-secondary no-underline flex items-center cursor-pointer whitespace-nowrap hover:bg-surface-4 hover:border-border-strong hover:text-text-primary"
      >
        Channels
      </a>
    {/if}
    {#if hasPermission(currentUser, 'can_all')}
      <a
        href="/admin/entities"
        class="h-7 px-3 text-sm bg-surface-3 border border-border-default rounded text-text-secondary no-underline flex items-center cursor-pointer whitespace-nowrap hover:bg-surface-4 hover:border-border-strong hover:text-text-primary"
      >
        Entities
      </a>
    {/if}
    {#if hasPermission(currentUser, 'can_manage_users')}
      <a
        href="/users"
        class="h-7 px-3 text-sm bg-surface-3 border border-border-default rounded text-text-secondary no-underline flex items-center cursor-pointer whitespace-nowrap hover:bg-surface-4 hover:border-border-strong hover:text-text-primary"
      >
        Users
      </a>
    {/if}
    {#if hasPermission(currentUser, 'can_view_graph')}
      <a
        href="/graph"
        class="h-7 px-3 text-sm bg-surface-3 border border-border-default rounded text-text-secondary no-underline flex items-center cursor-pointer whitespace-nowrap hover:bg-surface-4 hover:border-border-strong hover:text-text-primary"
      >
        Graph
      </a>
    {/if}

    {#if currentUser}
      <span class="flex items-center gap-2 text-xs text-text-muted">
        <span class="max-w-[100px] overflow-hidden text-ellipsis whitespace-nowrap">{currentUser.name}</span>
        <button
          class="py-1 px-2 text-xs bg-transparent border border-border-default text-text-muted rounded cursor-pointer hover:bg-surface-3 hover:text-text-primary"
          onclick={onLogout}
        >
          Log out
        </button>
      </span>
    {:else if videoData?.transcript}
      <button
        class="h-7 px-3 text-sm bg-accent-primary border-none rounded text-white cursor-pointer whitespace-nowrap hover:bg-accent-hover"
        onclick={onShowLogin}
      >
        Log in to analyze
      </button>
    {:else}
      <button
        class="h-7 px-3 text-sm bg-surface-3 border border-border-default rounded text-text-secondary cursor-pointer whitespace-nowrap hover:bg-surface-4 hover:border-border-strong hover:text-text-primary"
        onclick={onShowLogin}
      >
        Log in
      </button>
    {/if}
  </div>
</header>
